import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

import { differenceInDays } from "date-fns";
import { keyBy } from "lodash";

import { API, graphqlOperation } from "aws-amplify";
import {
  countInboundShipments,
  inboundShipmentByAccountByPoNumber,
} from "../../../graphql/inbound/custom-queries";
import { productByAccountByActiveByName } from "../../../graphql/queries";
import { getInventorieAggregate } from "../../../graphql/inventory/custom-queries";
import {
  buildTrackCarCreateInput,
  buildTrackCarUpdateInput,
  buildInboundScheduleUpdateInput,
  performUpdateInboundMini,
  sanitizeInbound,
  sanitizeInboundLambda,
} from "../../../graphql/inbound/inboundCRUD";

import { Grid, InputAdornment, Tooltip, Typography } from "@material-ui/core";

import { useNotify, Button } from "react-admin";

import {
  CarrierType,
  InboundState,
  InboundStatus,
  InventoryStatus,
  LocationType,
  LotType,
  NoteType,
  OutboundStatus,
  PODDefaultSubject,
  ResourceType,
  ShipmentLineItemStatus,
  SORTKEY_BUFFER,
  Unit,
  VALIDATION_MSG,
} from "../../../utils/constants";
import {
  BillingAutomation,
  ChargeEvent,
  StorageFrequency,
} from "../accounts/chargeRules/chargeRuleConstants";
import {
  fetchAllPages,
  logError,
  retrieveStripeSubscription,
} from "../../../utils/appUtils";

import {
  AddIcon,
  AddAllIcon,
  InfoOutlinedIcon,
  UploadIcon,
} from "../../../components/common/iconography";
import {
  getCustomFieldId,
  getCustomFieldsToSave,
  parseCustomField,
} from "../../../components/common/CustomFields";
import { ProductLinkIcon } from "../../../components/common/ProductLinkIcon";
import {
  convertUnits,
  getUnitConversions,
  getWeightUOM,
  getYardCommodity,
  isBulkCommodity,
  isBulkShipment,
  isWeightUOM,
  getUnitName,
} from "../../../utils/loadTypeUtils";
import {
  createLambdaAppointment,
  deleteLambdaAppointment,
  toComparableScheduledDateTime,
  updateLambdaAppointment,
} from "../../../utils/dateUtils";
import {
  trimDecimal,
  splitEmails,
  generateEmailSubject,
  formatCurrency,
  normalizeValue,
} from "../../../utils/utils";
import {
  autogenLotNumber,
  getPerpetualDate,
  isAwaitingCarlotNumber,
} from "../../../utils/lotNumberUtils";
import {
  filterAreas,
  filterZones,
  getLocationFullName,
  getTrackArea,
  isBulkLocation,
  isTrack,
} from "../../../utils/locationUtils";
import { sendEmail } from "../../../utils/zapierUtils";
import { isPlaceholderTrailer } from "../../../utils/placeholderTrailer";

import {
  getLatestShippedOutboundId,
  resetCapacityUsed,
} from "../inventory/InventoryUitls";
import { getUnitPrice } from "../services/ServiceUtils";
import { getTotalQuantityAvailable } from "../products/ProductUtils";
import { CatalogSearchDialog } from "./CatalogSearchDialog";
import { ManifestUploadDialog } from "./ManifestUploadDialog";
import { getInboundPODBody } from "./POD/PODBody";
import { createLineItemsForInbound } from "../invoices/InvoiceUtils";

/**
 * Compute auto charges for an inbound from form values or record and charge rules.
 * Use in InboundCreate (values, chargeRules), InboundEdit (values, chargeRules, record), or InboundShow (record, chargeRules).
 * @param {Object} inbound - Form values or inbound record
 * @param {Array} chargeRules - Account charge rules
 * @returns {{ items: Array }}
 */
export const getAutoCharges = (inbound, chargeRules) => {
  if (!inbound || !chargeRules?.length) {
    return { items: [] };
  }
  const inventories = inbound?.inventories?.items || [];
  const totalUOM = inbound?.totalUOM;

  const inboundLike = {
    id: inbound?.id ?? "preview",
    inventories: {
      items: inventories.map((inv) => ({
        ...inv,
        quantityReceived: inv.quantityReceived ?? inv.quantityAdvised ?? 0,
        inventoryUOM: totalUOM,
      })),
    },
    carrierType: inbound?.carrierType,
    carrierEquipmentTypeId: inbound?.carrierEquipmentTypeId,
    placedDateTime: inbound?.placedDateTime,
    collectedDateTime: inbound?.collectedDateTime,
    releaseDateTime: inbound?.releaseDateTime ?? new Date(),
    totalUOM: totalUOM,
    areaId: inbound?.areaId,
    zoneId: inbound?.zoneId,
    reloadYardCommodityId: inbound?.reloadYardCommodityId,
  };

  try {
    const autoRules = chargeRules.filter(
      (rule) => rule.execution === BillingAutomation.AUTO,
    );
    const autoOnly = createLineItemsForInbound(inboundLike, autoRules);
    return { items: autoOnly };
  } catch (error) {
    logError("inboundShipments:InboundUtils:getAutoCharges", error, false);
    return { items: [] };
  }
};

export const formValidate = (form, notify) => {
  const formState = form.getState();
  if (formState?.invalid) notify(VALIDATION_MSG, "warning");
  return formState?.invalid !== true;
};

export const fetchDuplicateInbounds = async (formData) => {
  const poNumber = formData.poNumber;
  const accountId = formData.accountId;
  if (!accountId || !poNumber) return false;
  const normalizedPoNumber = normalizeValue(poNumber);
  if (!normalizedPoNumber) return false;

  try {
    const filter = formData.id ? { id: { ne: formData.id } } : undefined;

    const response = await API.graphql(
      graphqlOperation(inboundShipmentByAccountByPoNumber, {
        accountId,
        normalizedPoNumber: { eq: normalizedPoNumber },
        filter,
        limit: 2,
      }),
    );

    return (
      response?.data?.inboundShipmentByAccountByPoNumber?.items?.length > 0
    );
  } catch (error) {
    logError(
      "inboundShipments:inboundUtils:getDuplicateInboundByPoNumber",
      error,
      false,
    );
    return false;
  }
};

export const validatePoNumberUnicity = async (formData, notify) => {
  try {
    const duplicateInbound = await fetchDuplicateInbounds(formData);
    if (duplicateInbound && notify) {
      notify("Duplicate PO Number", "warning", {
        autoHideDuration: 15000, // in milliseconds (15 seconds)
      });
    }

    return duplicateInbound;
  } catch (error) {
    logError(
      "inboundShipments:inboundUtils:validatePoNumberUnicity",
      error,
      false,
    );
    return false;
  }
};

/**
 * Search filter for "this car/trailer number is already open for this account".
 *
 * Only records still at the yard count. Cars come back around under the same
 * number, and warning on every return trained users to dismiss the warning
 * unread. A record has left when it is:
 * - RELEASED, or
 * - pulled in Track Management: a pull sets isCollected and leaves the status
 *   PLACED until someone clicks Set Released. (Time Collected can only be typed
 *   in after release, so isCollected + RELEASED covers every way out.)
 * - a truck that finished unloading (state COMPLETED). Trucks rarely reach
 *   RELEASED, which only invoicing yards set. A rail car that is COMPLETED but
 *   not pulled is still on its track, so it still counts.
 * ARCHIVED/AGGREGATED are the synthetic bulk-aggregate rows, never a real car.
 *
 * Returns null (skip the check) when the number is blank or a placeholder
 * such as "NA", which would match every other placeholder entry.
 */
export const buildOpenCarDuplicateFilter = ({ accountId, railCarNumber, id }) => {
  if (!accountId || !railCarNumber || isPlaceholderTrailer(railCarNumber))
    return null;

  const filter = {
    accountId: { eq: accountId },
    railCarNumber: { eq: railCarNumber },
    and: [
      { status: { ne: InboundStatus.RELEASED } },
      { status: { ne: InboundStatus.ARCHIVED } },
      { status: { ne: InboundStatus.AGGREGATED } },
      { isCollected: { ne: true } },
      {
        or: [
          { carrierType: { ne: CarrierType.TRUCK } },
          { state: { ne: InboundState.COMPLETED } },
        ],
      },
    ],
  };
  if (id) filter.id = { ne: id };
  return filter;
};

export const validateRailCarNumberUnicity = async (formData, notify) => {
  const filter = buildOpenCarDuplicateFilter(formData);
  if (!filter) return;

  try {
    const response = await API.graphql(
      graphqlOperation(countInboundShipments, {
        filter: filter,
        limit: 1,
      }),
    );

    const isDuplicate = response?.data?.searchInboundShipments?.total > 0;
    if (isDuplicate) {
      notify("Duplicate Railcar Number", "warning", {
        autoHideDuration: 15000, // in milliseconds (15 seconds)
      });
    }
  } catch (error) {
    logError(
      "inboundShipments:inboundUtils:validateRailcarNumberUnicity",
      error,
      false,
    );
  }
};

/**
 * Save-time duplicate hints for create/edit confirm dialogs (ValidateCreateButton, DialogSaveButton).
 * Lot number is not checked — duplicate lots are valid for the business.
 * PO uses fetchDuplicateInbounds (raw + normalized); rail/trailer uses
 * buildOpenCarDuplicateFilter (same account, still at the yard, exclude id).
 */
export const validateInboundUnicity = async (formData) => {
  const { accountId, poNumber, railCarNumber } = formData;
  const duplicateNumbers = [];

  if (!accountId || (!poNumber && !railCarNumber)) {
    return duplicateNumbers;
  }

  try {
    const poDuplicatePromise = poNumber
      ? fetchDuplicateInbounds(formData)
      : Promise.resolve(false);

    const railCarFilter = buildOpenCarDuplicateFilter(formData);
    const railCarNumberQuery = railCarFilter
      ? API.graphql(
          graphqlOperation(countInboundShipments, {
            filter: railCarFilter,
            limit: 1,
          }),
        )
      : Promise.resolve(null);

    const [poDuplicate, railCarNumberResponse] = await Promise.all([
      poDuplicatePromise,
      railCarNumberQuery,
    ]);

    const railIsDuplicate =
      (railCarNumberResponse?.data?.searchInboundShipments?.total || 0) > 0;

    if (poDuplicate) duplicateNumbers.push("PO");
    if (railIsDuplicate) duplicateNumbers.push("Car/Trl");
  } catch (error) {
    logError("inboundShipments:inboundUtils:validateUnicity", error, false);
  }

  return duplicateNumbers;
};

export const validateRailcarRelease = (data, newStatus) => {
  if (data.status !== OutboundStatus.RELEASED && isTrack(data.areaId)) {
    if (data.releaseDateTime || newStatus === OutboundStatus.RELEASED) {
      return hasInventoryRemaining(data);
    }
  }
  return false;
};

// used for bulk railcar release to zero out inventory heel
const hasInventoryRemaining = (record) => {
  const totalQuantity = record.inventories?.items?.reduce(
    (total, inventory) => {
      return total + (inventory.quantityOnHand || 0);
    },
    0,
  );

  return totalQuantity > 0 ? `${totalQuantity} ${record.totalUOM}` : false;
};

/**
 * The inventories a railcar release actually reaches, keyed off where the
 * stock sits rather than the shipment header — the header keeps the receiving
 * track area even after the stock was unloaded into the yard, so trusting it
 * holds the car hostage to picks the release could not touch.
 *
 * Two inventories are reached:
 *
 *   - every inventory of a SIT car, which the release closes out as RELEASED
 *   - stock still in the car, which the release either closes out (bulk goes
 *     SHIPPED) or leaves STORED on a track with no car under it, stranding
 *     any pick against it
 *
 * Both see transformLamdaInventories and utils/releasedRailcar.js. Stock
 * unloaded into the yard is ordinary yard stock: the release leaves it alone,
 * and committing it to an outbound is normal.
 */
const getReleaseAffectedInventories = (record) =>
  (record?.inventories?.items || []).filter(
    (inventory) =>
      Boolean(record?.sit) || isInventoryStoredOnTrack(inventory, record),
  );

const hasCommittedQuantity = (inventories) =>
  inventories.reduce(
    (total, inventory) => total + (inventory?.quantityCommitted || 0),
    0,
  ) > 0;

// A line item still holds its inventory until the load actually ships; every
// other status is an outbound the yard is still expected to load.
const isOpenLineItem = (lineItem) =>
  Boolean(lineItem) && lineItem.status !== ShipmentLineItemStatus.SHIPPED;

const collectOutboundLabels = (inventories) => {
  const labels = new Set();

  inventories.forEach((inventory) => {
    (inventory?.shipmentLineItems?.items || [])
      .filter(isOpenLineItem)
      .forEach((lineItem) => {
        const label =
          lineItem.outboundShipment?.pickupNumber ||
          lineItem.outboundShipmentId;
        if (label) labels.add(label);
      });
  });

  return [...labels];
};

/**
 * The outbound shipments the release would disturb, labelled by pickup number
 * when the caller's query loaded one (the dispatch form does) and by id
 * otherwise. Empty when nothing the release reaches is committed.
 */
export const getCommittedOutboundLabels = (record) =>
  collectOutboundLabels(getReleaseAffectedInventories(record));

/**
 * Refuses a railcar release that would ship inventory already committed to an
 * outbound pick. Release closes the car's inventory out — bulk stored on track
 * goes SHIPPED, a SIT car goes RELEASED (see transformLamdaInventories) — so
 * the committed quantity would vanish from under the outbound, leaving a pick
 * list pointing at inventory the yard no longer has. Unlike the remaining
 * on-hand heel, which the manager may knowingly write off, this is a hard
 * block: the inventory has to come off the outbound first.
 *
 * Only the inventory the release reaches counts
 * (getReleaseAffectedInventories). Stock unloaded off the car into the yard
 * survives the release untouched, so an outbound holding it must not keep an
 * emptied car in the yard.
 *
 * Rail only. A truck inbound's release does not deplete what it dropped, and
 * committing that yard inventory to an outbound is ordinary.
 *
 * @param {object} data - Shipment record or dispatch form values.
 * @param {string} [newStatus] - Status the caller is advancing to, when the
 *   record does not carry the release stamp yet.
 * @returns {string|null} Message to show the manager, or null when the release
 *   may go ahead.
 */
export const validateReleaseCommitment = (data, newStatus) => {
  if (!data) return null;

  const isReleasing =
    data.status !== InboundStatus.RELEASED &&
    (Boolean(data.releaseDateTime) || newStatus === InboundStatus.RELEASED);
  if (!isReleasing) return null;

  const onRail =
    Boolean(data.sit) || isTrack(data.areaId) || isTrack(data.area);
  if (!onRail) return null;

  const affected = getReleaseAffectedInventories(data);
  const labels = collectOutboundLabels(affected);
  if (!hasCommittedQuantity(affected) && !labels.length) return null;

  const outbounds = labels.length ? ` (${labels.join(", ")})` : "";
  return (
    `Inventory committed to a pending Outbound shipment${outbounds}. ` +
    "Unable to release until inventory is removed from the Outbound."
  );
};

/**
 * Boolean form of {@link validateReleaseCommitment}, for the controls that only
 * need to know whether a release may be offered at all.
 */
export const isReleaseBlockedByCommitment = (record) =>
  Boolean(validateReleaseCommitment(record, InboundStatus.RELEASED));

// Advice origin. `advisedBy` (the submitter's email, used as the approve/reject
// notification address) doubles as the origin marker: only the external portals
// set it — the manager advice create deliberately does not. So a missing
// advisedBy means the yard advised itself, and there is nobody to notify or
// return the advice to.
// IMPORTANT: do not start stamping advisedBy on manager-created advices; that
// would silently erase this signal.
export const isExternalAdvice = (record) => Boolean(record?.advisedBy);

const isBlankValue = (value) =>
  value === null || value === undefined || String(value).trim() === "";

// Values of a product's own custom fields, keyed by custom-field id. Product
// custom fields are encoded "name||value" strings sharing the account's
// inventoryCustomFields id space, so the keys line up with the import's field
// map. Blank values are dropped: they are placeholders, not defaults.
const getProductCustomFieldValues = (product) => {
  const values = {};
  product?.customFields?.forEach((field) => {
    const { name, value } = parseCustomField(field);
    if (!name || isBlankValue(value)) return;
    values[getCustomFieldId(name)] = String(value).trim();
  });
  return values;
};

/**
 * Custom fields for one imported inventory row, as encoded "name||value".
 *
 * The file wins. On a stock import the matched product's own value fills a
 * field the file left blank, so imported stock carries the same product
 * defaults it would get from the catalog picker. A manifest describes goods in
 * transit, so product defaults are not assumed there.
 *
 * A field with neither a mapped column nor a product value is left out, as it
 * always was; getCustomFieldsToSave adds it back blank at save time.
 */
export const buildImportCustomFields = ({
  row,
  fieldMap,
  accountFields,
  product,
  isStockImport,
}) => {
  const productValues = isStockImport
    ? getProductCustomFieldValues(product)
    : {};
  const customFields = [];

  accountFields?.forEach((customField) => {
    const customColumn = fieldMap?.[customField.id];
    const fileValue = customColumn ? row?.[customColumn] : undefined;
    const value = isBlankValue(fileValue)
      ? productValues[customField.id]
      : String(fileValue).trim();

    if (value === undefined && !customColumn) return;

    customFields.push(`${customField.name}||${value ?? ""}`);
  });

  return customFields.length > 0 ? customFields : undefined;
};

export const AddCatalogUploadButtons = ({
  backPath,
  form,
  formData,
  account,
  yardCommodity,
  ...props
}) => {
  const notify = useNotify();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [allProductsMap, setAllProductsMap] = useState({});
  const inventoryFieldState = form.getFieldState("inventories.items");
  const hasInventory = inventoryFieldState?.value?.length > 0;

  useEffect(() => {
    if (account) getProducts(account);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  const getProducts = async (account) => {
    try {
      // A limit is a page size, not a total: an account with more products
      // than one page would otherwise be missing rows from the catalog.
      const items = await fetchAllPages({
        query: productByAccountByActiveByName,
        variables: {
          accountId: account.id,
          activeName: { beginsWith: { active: "true" } },
        },
        responseKey: "productByAccountByActiveByName",
        pageLimit: 500,
        label: "InboundUtils:products",
      });
      // Enrich once at the source so consumers (catalog dialog grid) can use a
      // plain field instead of recomputing available per render.
      const products = items.map((product) => ({
        ...product,
        totalQuantityAvailable: getTotalQuantityAvailable(product),
      }));
      const productMap = keyBy(products, "id");
      setAllProducts(products);
      setAllProductsMap(productMap);
    } catch (error) {
      logError("inboundShipment:AddCatalogButtons:getProducts", error, notify);
    }
  };

  const NEW_INVENTORY = {
    status: InventoryStatus.NEW,
    reloadYardCommodityId: yardCommodity?.id,
    inventoryUOM: formData.totalUOM,
    weightUOM: account?.defaultWeightUOM,
    aisleId: "",
  };

  const handleAdd = () => {
    debugger;
    // handle manual inventory add
    if (inventoryFieldState?.value) {
      form.change("inventories.items", [
        ...inventoryFieldState.value,
        NEW_INVENTORY,
      ]);
    } else {
      form.change("inventories.items", [NEW_INVENTORY]);
    }
  };

  const handleAddSelected = (listContext) => {
    // add items selected from product catalog
    debugger;
    const formData = form.getState().values;
    const inventoriesState = form.getFieldState("inventories.items");
    let formInventories = inventoriesState?.value ? inventoriesState.value : [];

    let selectedProducts = listContext.selectedIds.map((selectedId) => {
      const product = allProductsMap[selectedId];

      let newInventory = {
        ...NEW_INVENTORY,
        productId: product.id,
        productCode: product.productCode,
        productName: product.name,
        reloadYardCommodityId: product.reloadYardCommodityId,
        inventoryUOM: product.defaultUOM,
        product: product,
        customFields: getCustomFieldsToSave(
          product.customFields,
          account.inventoryCustomFields,
        ),
      };

      const defaultWeightUOM =
        product.defaultWeightUOM || account.defaultWeightUOM;
      if (defaultWeightUOM) {
        newInventory.weightUOM = defaultWeightUOM;
        newInventory.weight = product[defaultWeightUOM.toLowerCase()];
        form.change("weightUnits", defaultWeightUOM);
        if (!formData.scaleWeightUnits) {
          form.change("scaleWeightUnits", defaultWeightUOM);
        }
      } else if (isWeightUOM(newInventory.inventoryUOM)) {
        // if inventory is measured in weight units, default weightUnits to inventoryUOM
        newInventory.weightUOM = newInventory.inventoryUOM;
        newInventory.weight = 1;
        form.change("weightUnits", newInventory.inventoryUOM);
        if (!formData.scaleWeightUnits) {
          form.change("scaleWeightUnits", newInventory.inventoryUOM);
        }
      }

      form.change("totalUOM", product.defaultUOM);

      return newInventory;
    });

    if (selectedProducts?.length > 0) {
      formInventories = [...formInventories, ...selectedProducts];
      form.change("inventories.items", formInventories);
    }
  };

  const handleAddUpload = (csvData, fieldMap, isStockImport, locationMap) => {
    // handle inventories uploaded from excel
    debugger;
    const formState = form.getState();
    const inventoriesState = formState.values.inventories?.items;
    let formInventories = inventoriesState?.value ? inventoriesState.value : [];
    const poColumn = fieldMap["poNumber"];
    const productCodeColumn = fieldMap["productCode"];
    const productNameColumn = fieldMap["productName"];
    const quantityColumn = fieldMap["quantityAdvised"];
    const weightColumn = fieldMap["weight"];
    const pcsColumn = fieldMap["pcs"]; // PPU field
    const railCarColumn = fieldMap["railCarNumber"];
    const lotNumberColumn = fieldMap["lotNumber"];
    const locationColumn = fieldMap["location"];

    let poMismatchCount = 0;
    const loadTypeMismatchCodes = new Set();

    if (!isStockImport && poColumn) {
      const rowCount = csvData.length;
      csvData = csvData.filter(
        (row) => row[poColumn] === formState.values.poNumber,
      );
      poMismatchCount = rowCount - csvData.length;
    }

    const inventoryRecords = csvData
      .map((row) => {
        const productCode = row[productCodeColumn];
        let product;
        if (productCode) {
          product = allProducts.find((itm) => itm.productCode === productCode);
        }

        // Product Load Type may differ from the shipment's selected Load Type.
        // The row keeps the product's own reloadYardCommodityId (stamped in the
        // product block below), same as adding it from the catalog dialog —
        // putaway filters areas/zones off the inventory row, not the header.
        // Recorded here only so the import can report it.
        if (product && product.reloadYardCommodityId !== yardCommodity?.id) {
          loadTypeMismatchCodes.add(productCode);
        }

        let newInventory = {
          ...NEW_INVENTORY,
          productName: row[productNameColumn],
          quantityAdvised: Number(row[quantityColumn]),
          weight: NEW_INVENTORY.weightUOM
            ? Number(row[weightColumn])
            : undefined,
        };

        if (isStockImport) {
          // Always stamp inbound PO on each inventory for traceability
          if (poColumn) {
            const raw = row[poColumn];
            if (raw != null && String(raw).trim() !== "")
              newInventory.poNumber = String(raw).trim();
          }
          if (railCarColumn) {
            const raw = row[railCarColumn];
            if (raw != null && String(raw).trim() !== "")
              newInventory.railCarNumber = String(raw).trim();
          }
          if (lotNumberColumn) {
            const raw = row[lotNumberColumn];
            if (raw != null && String(raw).trim() !== "")
              newInventory.lotNumber = String(raw).trim();
          }
        }

        if (
          locationColumn &&
          row[locationColumn] != null &&
          String(row[locationColumn]).trim() !== ""
        ) {
          const locationFullName = String(row[locationColumn]).trim();
          const location = locationMap[locationFullName];
          if (location) {
            newInventory.locationFullName = getLocationFullName(location);
            newInventory.locationId = location.id;
            newInventory.aisleId = location.aisleId;
            newInventory.zoneId = location.zoneId;
            newInventory.areaId = location.areaId;
          }
        }

        // Handle custom fields
        newInventory.customFields = buildImportCustomFields({
          row,
          fieldMap,
          accountFields: account.inventoryCustomFields,
          product,
          isStockImport,
        });

        // Handle product fields
        if (product) {
          newInventory.productId = product.id;
          newInventory.productCode = product.productCode;
          newInventory.productName = newInventory.productName || product.name;
          newInventory.product = product;
          newInventory.inventoryUOM = product.defaultUOM;
          newInventory.reloadYardCommodityId = product.reloadYardCommodityId;

          const weightUOM = product.defaultWeightUOM || newInventory.weightUOM;
          if (weightUOM) {
            newInventory.weightUOM = weightUOM;
            if (!row[weightColumn]) {
              const productWeight = product[weightUOM.toLowerCase()];
              newInventory.weight = Number(productWeight);
            }
          }
        }

        // Handle PCS / PPU value
        if (pcsColumn && row[pcsColumn]?.trim()) {
          const pcs = parseInt(row[pcsColumn]);
          if (!isNaN(pcs) && pcs > 0) {
            newInventory.pcs = pcs;
          }
        }

        return newInventory;
      })
      .filter((item) => item !== null);

    const skippedReasons = [];
    if (poMismatchCount > 0)
      skippedReasons.push(
        `${poMismatchCount} row(s) skipped: PO Number does not match "${formState.values.poNumber}"`,
      );
    if (skippedReasons.length > 0)
      notify(`${skippedReasons.join(". ")}.`, "warning");

    if (loadTypeMismatchCodes.size > 0) {
      const codes = [...loadTypeMismatchCodes];
      const shown = codes.slice(0, 5).join(", ");
      const more = codes.length > 5 ? `, +${codes.length - 5} more` : "";
      notify(
        `${codes.length} product code(s) added with a Load Type that differs from the shipment (${shown}${more}). Inventory keeps the product's Load Type.`,
        "warning",
      );
    }

    if (inventoryRecords?.length > 0) {
      formInventories = [...formInventories, ...inventoryRecords];
      form.change("inventories.items", formInventories);
    }

    // Add or replace manifest file in attachments
    const manifestFileField = form.getFieldState("manifestFile")?.value;
    if (manifestFileField) {
      let existingAttachments = form.getFieldState("attachments")?.value || [];
      // Remove any previous manifest file if present (assumes same file name means same manifest)
      existingAttachments = existingAttachments.filter(
        (att) => att.title !== manifestFileField.title,
      );
      const updatedAttachments = [...existingAttachments, manifestFileField];
      form.change("attachments", updatedAttachments);
    }

    handleInventoriesQty(form);
  };

  const isAddDisabled = () => {
    if (!account) return "Account must be selected.";
    if (!yardCommodity) return "Load Type must be selected";
    if (isBulkCommodity(yardCommodity))
      return "Bulk inventory must be linked to product.";
    return false;
  };

  const isCatalogDisabled = () => {
    if (!account) return "Account must be selected.";
    if (!yardCommodity?.commodity) return "Load Type must be selected";
    if (isBulkCommodity(yardCommodity) && hasInventory)
      return "Only one bulk inventory allowed.";
    return false;
  };

  return (
    <Grid container direction="row" spacing={1} alignItems="flex-start">
      <Grid item xs={4}>
        {isAddDisabled() ? (
          <Tooltip title={isAddDisabled()} placement="right">
            <span>
              <Button label="Add" color="default" disabled>
                <AddIcon />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button label="Add" color="default" onClick={handleAdd}>
            <AddIcon />
          </Button>
        )}
      </Grid>
      <Grid item xs={4}>
        {isCatalogDisabled() ? (
          <Tooltip title={isCatalogDisabled()} placement="right">
            <span>
              <Button
                label="Catalog"
                color="default"
                disabled
                style={{ marginLeft: "-15px" }}
              >
                <AddAllIcon />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            label="Catalog"
            color="default"
            style={{ marginLeft: "-15px" }}
            onClick={() => setIsSearchOpen(true)}
          >
            <AddAllIcon />
          </Button>
        )}
      </Grid>
      <Grid item xs={4}>
        {isAddDisabled() ? (
          <Tooltip title={isAddDisabled()} placement="right">
            <span>
              <Button label="Upload" color="default" disabled>
                <UploadIcon />
              </Button>
            </span>
          </Tooltip>
        ) : (
          <Button
            label="Upload"
            color="default"
            onClick={() => setUploadOpen(true)}
          >
            <UploadIcon />
          </Button>
        )}
      </Grid>
      <Grid item xs={false}>
        {account && yardCommodity && isSearchOpen && (
          <CatalogSearchDialog
            backPath={backPath}
            form={form}
            account={account}
            allProducts={allProducts}
            yardCommodity={yardCommodity}
            isOpen={isSearchOpen}
            setIsOpen={setIsSearchOpen}
            handleAddSelected={handleAddSelected}
          />
        )}
      </Grid>
      <Grid item xs={false}>
        {account && yardCommodity && (
          <ManifestUploadDialog
            form={form}
            account={account}
            isOpen={uploadOpen}
            setIsOpen={setUploadOpen}
            handleAddUpload={handleAddUpload}
          />
        )}
      </Grid>
    </Grid>
  );
};

// Moved to utils so shared placement paths (shipmentPlacement, the track
// hooks) don't have to import this manager-app component module; re-exported
// here for the inbound resource's existing callers.
export { autogenLotNumber, getPerpetualDate, isAwaitingCarlotNumber };

export const calcTotalQuantity = (formData) => {
  const tallySheet = formData.inventories?.items;
  const totalUOM = formData.totalUOM;
  let total = 0;
  if (!tallySheet) return total;
  tallySheet.forEach((item) => {
    const qty = Number(item?.quantityReceived || item?.quantityAdvised || 0);
    if (!qty) return;
    const itemUOM = item?.inventoryUOM;
    if (totalUOM && itemUOM && itemUOM !== totalUOM) {
      const result = convertUnits(qty, itemUOM, totalUOM, item?.product);
      total += !result.error && result.value != null ? result.value : qty;
    } else {
      total += qty;
    }
  });
  return total;
};

/**
 * Single weight unit for inventories: item.weightUOM or product.defaultWeightUOM.
 * Returns when all items share the same unit, or first item's unit as fallback.
 */
const getInventoryWeightUOM = (inventories) => {
  if (!inventories?.length) return null;
  const unitsFromItems = inventories.map(
    (inv) =>
      inv?.weightUnits || inv?.weightUOM || inv?.product?.defaultWeightUOM,
  );
  const unitsSet = new Set(unitsFromItems.filter(Boolean));
  if (unitsSet.size === 1) return unitsSet.values().next().value;
  return unitsFromItems.find(Boolean) ?? null;
};

/** Prefer primary weight units, else infer from inventories. */
const resolveWeightUOM = (primaryUnits, inventories) =>
  primaryUnits || getInventoryWeightUOM(inventories);

/**
 * Resolve weight per unit for one inventory in target units (toUnits).
 * Uses inventory.weight (with unit check/conversion) then product weight columns (e.g. product.lbs, product.tn).
 * @returns { number | null } weight in toUnits, or null if unavailable
 */
const getInventoryWeightInUnits = (inventory, toUnits) => {
  if (!toUnits) return null;
  const key = toUnits.toLowerCase();
  const inventoryWeight = inventory?.weight;
  const inventoryWeightUOM = inventory?.weightUOM;
  const product = inventory?.product;

  // 1. Use inventory.weight when it's in target units
  if (
    inventoryWeight != null &&
    inventoryWeight !== "" &&
    inventoryWeightUOM === toUnits
  ) {
    return Number(inventoryWeight);
  }
  // 2. Convert inventory.weight from inventory.weightUOM to toUnits (product or standard conversion)
  if (
    inventoryWeight != null &&
    inventoryWeight !== "" &&
    inventoryWeightUOM &&
    inventoryWeightUOM !== toUnits &&
    product
  ) {
    const conv = convertUnits(
      inventoryWeight,
      inventoryWeightUOM,
      toUnits,
      product,
    );
    if (!conv.error && conv.value != null) return conv.value;
  }
  // 3. Use product weight column for target units (e.g. product.lbs, product.tn)
  const productWeight = product?.[key];
  if (productWeight != null && productWeight !== "") {
    return Number(productWeight);
  }
  return null;
};

/**
 * Single place for net weight from inventories.
 * Options: { totalUOM, convert, setWeightError, weightUOMLabel }.
 * When convert is true, uses convertUnits(quantityAdvised, totalUOM, toUnits, product) per line.
 * Otherwise uses getInventoryWeightInUnits (inventory.weight + product weight columns, with inferred units).
 * Returns { total, error }. If error is set, total may still be partial; setWeightError is called when provided.
 */
const computeNetWeightFromInventories = (
  inventories,
  toUnits,
  options = {},
) => {
  const {
    totalUOM,
    convert,
    setWeightError,
    weightUOMLabel = toUnits,
  } = options;
  let total = 0;
  let error = null;
  let hasWeights = true;

  if (!toUnits || !inventories?.length) {
    return { total: null, error: null, hasWeights: false };
  }

  for (const inventory of inventories) {
    let weight = 0;
    let weightResolved = false;
    if (convert && totalUOM != null) {
      const unitValue = convertUnits(
        inventory.quantityAdvised,
        totalUOM,
        toUnits,
        inventory.product,
      );
      weight = unitValue.value;
      weightResolved = !unitValue.error && unitValue.value != null;
      if (!error && unitValue.error) {
        error = inventory.product
          ? `No conversion for ${weightUOMLabel}`
          : `Product missing entry for ${weightUOMLabel}`;
      }
    } else {
      const resolved = getInventoryWeightInUnits(inventory, toUnits);
      weight = resolved != null ? resolved : 0;
      weightResolved = resolved != null;
    }
    const qty =
      Number(inventory?.quantityAdvised) ??
      Number(inventory?.quantityReceived) ??
      0;
    hasWeights = hasWeights && weightResolved;
    if (hasWeights) {
      total +=
        (Number(weight) || 0) *
        (convert ? (inventory.quantityAdvised ?? 0) : qty);
    } else if (!error) {
      error = "Missing weight in inventories.";
    }
  }

  if (error && setWeightError) setWeightError(error);
  return {
    total: hasWeights && total >= 0 ? trimDecimal(total, 3) : null,
    error,
    hasWeights,
  };
};

/**
 * Compute total net weight from inventories.items (inv.weight or product weight columns).
 * Uses inbound.weightUnits, else getInventoryWeightUOM (inv.weightUOM or product.defaultWeightUOM).
 * Used for read-only display; for form calculation with conversion/errors use calcNetWeight.
 */
export const getTotalNetWeight = (inbound) => {
  const inventories = inbound?.inventories?.items;
  if (!inventories?.length) return { total: null, weightUOM: null };
  const weightUOM = resolveWeightUOM(inbound?.weightUnits, inventories);
  if (!weightUOM) return { total: null, weightUOM: null };
  const { total, error } = computeNetWeightFromInventories(
    inventories,
    weightUOM,
    {
      convert: false,
    },
  );
  return {
    total: error ? null : total,
    weightUOM,
  };
};

/**
 * Render string for total net weight (e.g. "1,234 lbs"). Uses getTotalNetWeight.
 */
export const renderTotalNetWeight = (record) => {
  const { total, weightUOM } = getTotalNetWeight(record);
  if (total == null || total < 0) return "";
  return `${Number(total).toLocaleString()} ${getUnitName(weightUOM)}`.trim();
};

export const calcNetWeight = (inbound, setWeightError, weightUOM) => {
  const inventories = inbound.inventories?.items;
  const inventoryWeightUOM = getInventoryWeightUOM(inventories);
  const toUnits = weightUOM || inbound.weightUnits || inventoryWeightUOM;
  const convert =
    toUnits &&
    (inbound.weightUnits
      ? toUnits !== inbound.weightUnits
      : toUnits !== inventoryWeightUOM);
  if (setWeightError) setWeightError(null);

  try {
    const { total, error, hasWeights } = computeNetWeightFromInventories(
      inventories,
      toUnits,
      {
        totalUOM: inbound.totalUOM,
        convert,
        setWeightError,
        weightUOMLabel: weightUOM,
      },
    );
    if (error) return null;
    if (hasWeights && total != null) return total;
    return inbound.netWeight;
  } catch (err) {
    logError("inboundUtils:calcNetWeight:", err, false);
    return null;
  }
};

const getComparableTimeslotSelection = (timeslot) => ({
  year: Number.isFinite(timeslot?.year) ? timeslot.year : null,
  month: Number.isFinite(timeslot?.month) ? timeslot.month : null,
  day: Number.isFinite(timeslot?.day) ? timeslot.day : null,
  time: timeslot?.time || null,
  eventTypeId: timeslot?.eventTypeId || timeslot?.eventType?.id || null,
});

export const shouldUpdateInboundTimeslot = (record, inbound) => {
  if (
    !record?.timeslotId ||
    !inbound?.timeslot ||
    !inbound?.scheduledDateTime
  ) {
    return false;
  }

  if (
    toComparableScheduledDateTime(record.scheduledDateTime) !==
    toComparableScheduledDateTime(inbound.scheduledDateTime)
  ) {
    return true;
  }

  const currentTimeslot = getComparableTimeslotSelection(record?.timeslot);
  const nextTimeslot = getComparableTimeslotSelection(inbound.timeslot);

  return (
    currentTimeslot.year !== nextTimeslot.year ||
    currentTimeslot.month !== nextTimeslot.month ||
    currentTimeslot.day !== nextTimeslot.day ||
    currentTimeslot.time !== nextTimeslot.time ||
    currentTimeslot.eventTypeId !== nextTimeslot.eventTypeId
  );
};

export const handleInventoriesWeight = (form, setWeightError) => {
  const formState = form.getState();
  const formData = formState.values;
  const weightUOM = getWeightUOM(formData.account, null);
  let totalWeight = null;
  if (weightUOM) {
    totalWeight = calcNetWeight(formData, setWeightError, weightUOM);
    form.change("weightUnits", weightUOM);
    form.change("netWeight", totalWeight);
  } else {
    form.change("weightUnits", null);
    form.change("netWeight", null);
  }
};

/**
 * Default POD email subject for a shipment, from the account template.
 * Used to prefill the Send dialog's Subject field.
 */
export const getPODEmailSubject = (record, account) =>
  generateEmailSubject(
    (account || record?.account)?.podSubject || PODDefaultSubject,
    record,
  );

export const emailPOD = async (
  record,
  toEmail,
  ccEmail,
  enclosePhotos,
  encloseAttachments,
  notify,
  subjectOverride,
) => {
  if (!toEmail) return false;
  const subject = subjectOverride || getPODEmailSubject(record);

  try {
    const htmlDoc = getInboundPODBody(
      record,
      enclosePhotos,
      encloseAttachments,
    );

    const emailBody = {
      from: "no_reply transloads.co <pod@transloads.co>",
      to: splitEmails(toEmail),
      cc: splitEmails(ccEmail),
      subject: subject,
      template: "emailTemplateWide.html",
      htmlContent: htmlDoc,
      attachPDF: true,
    };
    sendEmail(emailBody, "inboundShipment:inboundUtils:emailPOD", notify);
    return true;
  } catch (error) {
    logError("inboundShipment:inboundUtils:emailPOD", error, notify);
    return false;
  }
};

export const getInfoBubble = (formData, scopedFormData) => {
  const quantity =
    scopedFormData?.quantityReceived || scopedFormData?.quantityAdvised || 1;
  const inventory = scopedFormData;
  const product = inventory?.product;

  // Get commodity for determining calculation type
  const commodity =
    inventory?.reloadYardCommodityId ||
    formData?.reloadYardCommodityId ||
    inventory?.reloadYardCommodity?.commodity ||
    formData?.reloadYardCommodity?.commodity;

  const infoBubble = getUnitConversions({
    inventory,
    quantity,
    fromUnits: formData.totalUOM,
    commodity,
    product,
  });

  // Build tooltip content
  const hasContent = infoBubble.length > 0;

  // Show broken link icon if no product
  const showBrokenLink = !product;

  // Build tooltip title
  const tooltipTitle = (
    <>
      {product && (
        <Typography>Product Code: {scopedFormData.productCode}</Typography>
      )}
      {infoBubble.length > 0 && (
        <>
          <Typography>
            {quantity.toLocaleString()} {getUnitName(formData.totalUOM)} =
          </Typography>
          {infoBubble.map((itm) =>
            itm.value ? (
              <Typography key={itm.unit}>
                &emsp;{trimDecimal(itm.value, 3).toLocaleString()}{" "}
                {getUnitName(itm.unit)}
              </Typography>
            ) : null,
          )}
        </>
      )}
    </>
  );

  return (
    <InputAdornment position="end">
      {showBrokenLink && <ProductLinkIcon />}
      {hasContent && (
        <Tooltip title={tooltipTitle} placement="right">
          <InfoOutlinedIcon fontSize="small" />
        </Tooltip>
      )}
    </InputAdornment>
  );
};

export const handleInventoriesQty = (form) => {
  handleInventoriesWeight(form, undefined);
};

export const handleCommodityChange = (
  valueOrEvent,
  form,
  setYardCommodity,
  area,
  setAreas,
) => {
  debugger;
  // Support both SelectInput's event signature and AutocompleteInput's value signature.
  const value = valueOrEvent?.target?.value ?? valueOrEvent;
  const yardComm = getYardCommodity(value);

  setYardCommodity && setYardCommodity(yardComm);
  form.change("reloadYardCommodity", yardComm);
  form.change("totalUOM", yardComm?.units || "");

  const areas = filterAreas(window.appUser.reloadYard.areas.items, yardComm);
  setAreas(areas);

  const formData = form.getState().values;
  if (!isContainerDropEnabled(formData, area)) {
    form.change("containerDrop", false);
  }
};

export const handleUnitChange = (form) => {
  const formState = form.getState();
  const formData = formState.values;
  const fieldState = form.getFieldState("inventories.items");
  const inventories = fieldState?.value;

  const updatedInventories = inventories?.map((inv) => ({
    ...inv,
    inventoryUOM: formData.totalUOM,
  }));

  form.change("inventories.items", updatedInventories);
};

export const handleContainerDropChange = (value, form) => {
  if (value) {
    const formData = form.getState().values;
    form.change("secondaryReview", false);
    form.change("stockImport", false);
    form.change("lotType", LotType.CARLOT);
    form.change("lotNumberOverride", true);
    form.change("lotNumber", formData.containerNumber);
  }
};

export const handleContainerNumberChange = (value, form) => {
  const formData = form.getState().values;
  if (formData.lotNumberOverride) {
    form.change("lotNumber", value);
  }
};

export const hasAllProducts = (form, inbound) => {
  let retVal = true;

  if (inbound?.inventories?.items) {
    retVal = !inbound.inventories.items.some((item) => !item?.product);
  } else {
    const fieldState = form.getFieldState("inventories.items");
    const inventories = fieldState?.value;
    retVal = !inventories?.some((item) => !item?.product);
  }

  return retVal;
};

export const updateStripeUsage = async (inbound) => {
  // if (inbound.stockImport) return;
  try {
    debugger;
    let billingScheme = window.appUser.subscription?.plan?.usage_type;
    let subscriptionItem = window.appUser.subscription?.items?.data[0];

    if (!subscriptionItem) {
      // Retry get updated subscription in case of stripe error on login.
      const subscription = await retrieveStripeSubscription(
        window.appUser.reloadYard,
      );
      subscriptionItem = subscription?.items?.data[0];
      if (subscriptionItem) {
        window.appUser.subscription = subscription;
        billingScheme = subscription.plan?.billing_scheme;
      } else {
        logError(
          "inboundUtils:updateStripeUsage:response",
          subscription,
          false,
        );
        return;
      }
    }

    if (billingScheme === "metered") await runUpdateStripeUsage(inbound);
  } catch (error) {
    logError("inboundUtils:updateStripeUsage", error, false);
  }
};

const runUpdateStripeUsage = async (inbound) => {
  const response = await API.post("transREST", "/stripe-report-usage", {
    body: {
      subscriptionItemId: window.appUser.subscription.items.data[0].id,
      timestamp: inbound.stripeBillingDateTime
        ? new Date(inbound.stripeBillingDateTime).getTime()
        : new Date().getTime(),
      shipmentId: inbound.id,
      // shipmentId: crypto.randomUUID(), // for testing ONLY
    },
  });

  if (response.err) {
    logError("inboundUtils:runUpdateStripeUsage:response", response.err, false);
  }
};

// Example of how to do custom validate required
// export const requiredRailCarNunber = (value, allValues) => {
//     if (allValues?.carrierType === CarrierType.RAIL && (!value || value === "") ) {
//       // return an object just like the other validators
//       return { message: `Required`, isRequired: true, args: { required: true } }
//     }
//     return null
// };

const transformLambdaSeals = (inbound) => {
  const sealUpdateList = [];
  if (inbound?.inValidSeals?.items) {
    inbound.inValidSeals.items.forEach((el) => {
      const sealObject = inbound.seals.items.find((seal) => seal.id === el.id);

      if (
        el.barcode !== sealObject.barcode ||
        el.validated !== sealObject.validated
      ) {
        sealUpdateList.push(el);
      }
    });
  }
  return { sealUpdateList };
};

/**
 * Derive the appointment operation a save implies, and point `inbound`'s
 * schedule link at the result.
 *
 * The link is re-set on the passed object on purpose: the caller hands that
 * record back to react-admin's cache, and a stale timeslotId sends the next
 * save down processInbound's create-appointment branch, which fails its
 * attribute_not_exists condition.
 */
const buildInboundTimeslotOp = (record, inbound, updateDateTime) => {
  let timeslotInput = null;
  if (record.timeslotId) {
    if (!inbound.scheduledDateTime) {
      timeslotInput = deleteLambdaAppointment(record.timeslotId);
      timeslotInput.inboundId = record.id;
    } else if (shouldUpdateInboundTimeslot(record, inbound)) {
      timeslotInput = updateLambdaAppointment({
        ...inbound.timeslot,
        id: record.timeslotId,
        inboundId: record.id,
        updatedAt: updateDateTime,
      });
    }
  } else if (inbound.timeslot && inbound.scheduledDateTime) {
    // Both checked: a lingering `timeslot` object with a cleared appointment
    // date would otherwise fail createLambdaAppointment's guard and abort the save.
    timeslotInput = createLambdaAppointment(
      inbound.timeslot,
      record.id,
      null,
      // Prefer the form's account: if the user changed it in this save, the
      // persisted record still holds the old one, which would stamp the new
      // timeslot (and its group auth) with the wrong customer.
      inbound.accountId || record.accountId,
      inbound.accountName || record.accountName,
    );
  }

  if (timeslotInput) {
    // Mirror OutboundUtils: keep the caller's record (returned to the RA
    // cache) pointing at the post-save schedule link, or a re-save from the
    // cached record takes the wrong create/update path in processInbound.
    inbound.timeslotId =
      timeslotInput.transactType === "delete" ? null : timeslotInput.id;
  }

  return timeslotInput;
};

/**
 * Move an appointment and nothing else — the inbound twin of
 * updateLambdaOutboundSchedule.
 *
 * The carrier dispatch form has exactly one editable field, so its save has no
 * business rewriting the shipment header, the tally sheet or inventory — which
 * is what routing it through lambdaSaveInbound's full-record path did,
 * reverting any yard edit made while the form sat open. Only scheduledDateTime
 * travels from the form; every other value in the payload is read from
 * `record` (the loaded DB state), under a condition on the status and schedule
 * link it was read at.
 *
 * @param {object} record - previousData, i.e. the shipment as loaded
 * @param {object} inbound - the form's data (only scheduledDateTime/timeslot used)
 * @param {object[]} historyAppendList - notes to append, never a full history
 */
export const lambdaSaveInboundSchedule = async (
  record,
  inbound,
  { historyAppendList = [] } = {},
) => {
  const updateDateTime = new Date().toISOString();
  const timeslotInput = buildInboundTimeslotOp(record, inbound, updateDateTime);

  // The appointment did not move, so there is nothing this path may write:
  // re-stamping the header would be exactly the stale write it exists to avoid.
  //
  // Said out loud rather than resolved quietly. The caller's success handler
  // cannot tell a no-op from a write, so returning here announced "scheduled
  // successfully" over a save that wrote nothing — which matters most in the
  // case the carrier is trying to fix, where the stored appointment and the
  // timeslot row disagree and re-picking the slot appears to work but changes
  // nothing. userFacing routes it to the dialog the form already shows for
  // reasons a carrier can act on.
  if (!timeslotInput) {
    const unchanged = new Error(
      "This delivery is already scheduled for the time you picked — nothing to save.",
    );
    unchanged.userFacing = true;
    throw unchanged;
  }

  const inboundScheduleUpdateInput = buildInboundScheduleUpdateInput(
    { ...record, scheduledDateTime: inbound.scheduledDateTime },
    {
      timeslotId:
        timeslotInput.transactType === "delete" ? null : timeslotInput.id,
      expectedCurrentTimeslotId: record.timeslotId || null,
      // From `record`, never from the form: this is the appointment the carrier
      // was looking at when they hit Save. Rescheduling keeps the same timeslot
      // row, so without this a second form opened on the same booking passes
      // every condition and overwrites the first move without a word.
      expectedCurrentScheduledDateTime: record.scheduledDateTime || null,
      expectedStatus: record.status,
      historyAppendList,
    },
  );

  const response = await API.post("transREST", "/inboundUpdate", {
    body: {
      inboundFullUpdateInput: null,
      inboundUpdateInput: null,
      inboundScheduleUpdateInput,
      timeslotInput,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });

  if (response.error) {
    logError(
      "inboundShipments:inboundUtils:lambdaSaveInboundSchedule",
      response.error,
      false,
      false,
    );
    throw response.error;
  }
};

export const lambdaSaveInbound = async (
  record,
  inbound,
  isRollback,
  {
    fullUpdate = false,
    scheduleOnly = false,
    historyAppendList,
    trackCarPullId = null,
    trackCarRestoreId = null,
  } = {},
) => {
  // Schedule-only saves never touch inventory, seals, charges or the track
  // car: they get their own narrow, conditional transaction.
  if (scheduleOnly) {
    return await lambdaSaveInboundSchedule(record, inbound, {
      historyAppendList,
    });
  }

  const transData = await transformLamdaInventories(inbound, isRollback);
  const transDataSeal = transformLambdaSeals(inbound);

  const manualChargeOps = buildManualChargeOps(
    inbound,
    record?.manualCharges?.items,
    inbound?.manualCharges?.items,
  );
  // For testing purposes only
  const stripeUsageInput = {
    // subscriptionItemId: window.appUser.subscription.items.data[0].id,
    // timestamp: inbound.stripeBillingDateTime ? new Date(inbound.stripeBillingDateTime).getTime() : new Date().getTime(),
    // shipmentId: inbound.id,
    // shipmentId: crypto.randomUUID(), // for testing ONLY
  };

  let trackCarDeleteInput = null;
  let trackCarCreateInput = null;
  let trackCarUpdateInput = null;
  if (
    record.carrierType === CarrierType.RAIL &&
    inbound.carrierType === CarrierType.TRUCK &&
    record.id
  ) {
    trackCarDeleteInput = { id: record.id };
  } else if (
    (record.carrierType === CarrierType.TRUCK &&
      inbound.carrierType === CarrierType.RAIL &&
      !inbound.trackCar &&
      record.id) ||
    (record.status?.startsWith(InboundStatus.ADVICE_ALL) &&
      inbound.status === InboundStatus.IN_TRANSIT &&
      inbound.carrierType === CarrierType.RAIL)
  ) {
    // Use "now" for TrackCar createdAt (carrier change), not the shipment’s original createdAt
    const now = new Date().toISOString();
    trackCarCreateInput = buildTrackCarCreateInput({
      ...inbound,
      id: record.id,
      createdAt: now,
    });
  } else if (
    // Advice records (PROCESSING / REVIEW / FAILED / REJECTED) have no TrackCar
    // row yet — one is created on approval, above. Updating a non-existent row
    // fails buildTrackCarUpdateInput's attribute_exists(id) and aborts the
    // whole transaction, e.g. on a customer resubmit that edits railCarNumber.
    !record.status?.startsWith(InboundStatus.ADVICE_ALL) &&
    record.carrierType === CarrierType.RAIL &&
    inbound.carrierType === CarrierType.RAIL &&
    record.id &&
    record.railCarNumber !== inbound.railCarNumber
  ) {
    trackCarUpdateInput = buildTrackCarUpdateInput({
      ...inbound,
      id: record.id,
    });
  }

  const updateDateTime = new Date().toISOString();
  const timeslotInput = buildInboundTimeslotOp(record, inbound, updateDateTime);
  let inboundScheduleUpdateInput = null;

  if (timeslotInput) {
    inboundScheduleUpdateInput = buildInboundScheduleUpdateInput(inbound, {
      timeslotId:
        timeslotInput.transactType === "delete" ? null : timeslotInput.id,
      expectedCurrentTimeslotId: record.timeslotId || null,
    });
  }

  const response = await API.post("transREST", "/inboundUpdate", {
    body: {
      // Full-record parent write for the consolidated edit save (mirror of
      // Task 7's outboundFullUpdateInput). sanitizeInboundLambda strips
      // @connection objects but KEEPS the client-stamped updatedAt so the
      // direct DynamoDB write persists it (AppSync no longer stamps it).
      // Strict else-if in the lambda: never sent alongside inboundUpdateInput.
      inboundFullUpdateInput: fullUpdate
        ? sanitizeInboundLambda({ ...inbound })
        : null,
      inboundUpdateInput: null,
      inboundScheduleUpdateInput,
      timeslotInput: timeslotInput,
      inventoryUpdateList: transData.inventoryUpdateList,
      inventoryItemUpdateList: transData.inventoryItemUpdateList,
      inventoryItemDeleteList: transData.inventoryItemDeleteList,
      locationUpdateList: transData.locationUpdateList,
      sealUpdateList: transDataSeal.sealUpdateList,
      stripeUsageInput: stripeUsageInput,
      manualChargeCreateList: manualChargeOps.createList,
      manualChargeUpdateList: manualChargeOps.updateList,
      manualChargeDeleteList: manualChargeOps.deleteList,
      trackCarDeleteInput: trackCarDeleteInput,
      trackCarCreateInput: trackCarCreateInput,
      trackCarUpdateInput: trackCarUpdateInput,
      // Read and pulled or restored by the lambda inside this transaction; a
      // car already where the save wants it is left alone.
      trackCarPullInput: trackCarPullId ? { id: trackCarPullId } : null,
      trackCarRestoreInput: trackCarRestoreId ? { id: trackCarRestoreId } : null,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });

  if (response.error) {
    logError(
      "inboundShipments:inboundUtils:lambdaSaveInventories",
      response.error,
      false,
      false,
    );
    throw response.error;
  }
};

// Every member of the two composite index keys the lambda rebuilds on a
// header write (processInbound getInboundKeys).
const INBOUND_KEY_MEMBERS = [
  "status",
  "state",
  "railCarNumber",
  "releaseDateTime",
  "placedDateTime",
  "notifyDateTime",
  "scheduledDateTime",
  "createDateTime",
];

/**
 * A narrow header write through the lambda: the fields `changes` sets, plus
 * every composite-key member as `shipment` read them — the lambda rebuilds
 * both index keys from its input, and writes nothing it is not given. For a
 * caller that has to move the TrackCar in the same transaction, which an
 * AppSync update cannot.
 *
 * @param shipment a fresh read carrying every key member (null is a value;
 *   a field the query left out throws, rather than write a key from nothing)
 * @param changes the fields to set; they win over the read
 */
export const lambdaUpdateInboundHeader = async (
  shipment,
  changes,
  { trackCarRestoreId = null } = {},
) => {
  const missing = INBOUND_KEY_MEMBERS.filter((key) => !(key in shipment));
  if (missing.length) {
    throw new Error(
      `lambdaUpdateInboundHeader: ${shipment.id} read without ${missing.join(", ")}`,
    );
  }
  const keys = Object.fromEntries(
    INBOUND_KEY_MEMBERS.map((key) => [key, shipment[key]]),
  );

  const response = await API.post("transREST", "/inboundUpdate", {
    body: {
      inboundFullUpdateInput: {
        id: shipment.id,
        ...keys,
        ...changes,
        // AppSync stamped updatedAt; the direct lambda write does not.
        updatedAt: changes.updatedAt || new Date().toISOString(),
      },
      inboundUpdateInput: null,
      trackCarRestoreInput: trackCarRestoreId
        ? { id: trackCarRestoreId }
        : null,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });

  if (!response || response.error) {
    throw (
      (response && response.error) ||
      new Error("Invalid /inboundUpdate response")
    );
  }
  return response;
};

// used to save with old updatedAt timestamp
export const updateLambdaInboundMini = async (record) => {
  // sanitize updatedAt
  record.invoiceId = record.invoice?.id || " ";
  const inboundUpdateInput = sanitizeInbound(record);

  const response = await API.post("transREST", "/inboundUpdate", {
    body: {
      inboundUpdateInput: inboundUpdateInput,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
      // updatedAt: do not set - will generate inbound changed on BOL generate
    },
  });

  if (!response || response.error) {
    throw (
      (response && response.error) ||
      new Error("Invalid /inboundUpdate response")
    );
  }

  return response;
};

/**
 * True when the inventory's stock is still sitting in the railcar on the track.
 *
 * Keyed off the inventory items, never the inventory header: the header keeps
 * the receiving shipment's area and can disagree with where the stock actually
 * is (stock imports carrying their own locations, rail unloads, historical
 * drift). The caller closes the inventory out as SHIPPED and sweeps every item
 * with it, so a header that lies destroys stock that is really in the yard.
 *
 * Falls back to the shipment's own area for legacy inventories that never got
 * an item. A part-unloaded inventory (some items off track) reads as unloaded:
 * leaving it STORED is the recoverable outcome.
 */
export const isInventoryStoredOnTrack = (inventory, inbound) => {
  const items = inventory?.inventoryItems?.items ?? [];
  const storedItems = items.filter(
    (item) => item?.status === InventoryStatus.STORED,
  );
  const placedItems = storedItems.length > 0 ? storedItems : items;

  if (placedItems.length === 0) return isTrack(inbound?.areaId);

  return placedItems.every((item) => isTrack(item?.areaId));
};

export const transformLamdaInventories = async (inbound, isRollback) => {
  const updateDate = new Date().toISOString();
  const inventoryUpdateList = [];
  const inventoryItemUpdateList = [];
  const inventoryItemDeleteList = [];
  const toUpdateLocationMap = {};
  const toUpdateAggregateItemMap = {};
  const inboundZoneId = inbound.zoneId || " ";
  debugger;

  // Update only CURRENT Inventory Items
  const inventoryCount = inbound.inventories.items.length;
  for (let index = 0; index < inventoryCount; index++) {
    const inventory = inbound.inventories.items[index];
    let aggregateInventory = null;
    const inventoryZoneId = inventory.locationId
      ? inventory.zoneId || inboundZoneId
      : inboundZoneId;

    if (inventory.id) {
      const toUpdateInventory = {
        ...inventory,
        updatedAt: updateDate,
        areaId: inventory.locationId ? inventory.areaId : inbound.areaId,
        zoneId: inventoryZoneId,
        lotNumber:
          inbound.stockImport || inbound.returnShipment
            ? inventory.lotNumber
            : inbound.lotNumber,
        containerNumber:
          inbound.stockImport || inbound.returnShipment
            ? inventory.containerNumber
            : inbound.containerNumber,
        poNumber:
          inbound.stockImport || inbound.returnShipment
            ? inventory.poNumber
            : inbound.poNumber,
        railCarNumber:
          inbound.stockImport || inbound.returnShipment
            ? inventory.railCarNumber
            : inbound.railCarNumber,
        receivedAt: inbound.placedDateTime,
        inventoryUOM: inbound.totalUOM,
        quantityReceived: inventory.quantityReceived,
        quantityOnHand: inventory.quantityOnHand,
        quantityCommitted: inventory.quantityCommitted,
        quantityPhysical: inventory.quantityPhysical,
        sortKey: index + SORTKEY_BUFFER,
        customFields: getCustomFieldsToSave(
          inventory.customFields,
          inbound.account.inventoryCustomFields,
        ),
        photos: inventory.photos ? inventory.photos : null,
        verificationRequired: inventory.verificationRequired
          ? inventory.verificationRequired
          : null,
      };

      if (
        inbound.state === InboundState.NEW ||
        inbound.state === InboundState.PROCESS_SHIPMENT
      ) {
        // rollback inventory back to IN_TRANSIT
        if (!inbound.status.startsWith(InboundStatus.ADVICE_ALL)) {
          toUpdateInventory.status = InventoryStatus.IN_TRANSIT;
          toUpdateInventory.verificationRequired = null;
        }
        toUpdateInventory.quantityReceived = null;
        toUpdateInventory.quantityOnHand = 0;

        toUpdateInventory.hasException = false;
        toUpdateInventory.overShipment = false;
        toUpdateInventory.overShipmentQuantity = null;
        toUpdateInventory.overShipmentNote = null;
        toUpdateInventory.underShipment = false;
        toUpdateInventory.underShipmentQuantity = null;
        toUpdateInventory.underShipmentNote = null;
        toUpdateInventory.isDamaged = false;
        toUpdateInventory.originOfDamage = "";
        toUpdateInventory.otherException = false;
        toUpdateInventory.otherExceptionNote = null;
      } else if (
        inbound.state === InboundState.PROCESS_INVENTORY &&
        inventory.status === InventoryStatus.ARRIVED &&
        inventory.verificationRequired === "true"
      ) {
        // accepted = clear verificationRequired
        toUpdateInventory.status = InventoryStatus.ARRIVED;
        toUpdateInventory.verificationRequired = "resolved";
      } else if (
        isBulkCommodity(inbound.reloadYardCommodity) &&
        isInventoryStoredOnTrack(inventory, inbound) &&
        inbound.status === InboundStatus.RELEASED &&
        inventory.status === InventoryStatus.STORED
      ) {
        // zero out inventory on railcar release - for bulk commodities in railcar stored on track
        // toUpdateInventory.quantityOnHand = 0;   preserve discrepency count
        toUpdateInventory.status = InventoryStatus.SHIPPED;
        toUpdateInventory.depletedAt = updateDate;
        toUpdateInventory.lastOutboundId =
          getLatestShippedOutboundId(toUpdateInventory);
        toUpdateInventory.turnDays = differenceInDays(
          new Date(updateDate),
          new Date(inbound.placedDateTime),
        );
      } else if (inbound.sit && inbound.status === InboundStatus.RELEASED) {
        // SIT: rail release without the bulk STORED-on-track path — still close inventory as shipped.
        // Inventory lines below key off toUpdateInventory.status === RELEASED.
        // do not store depletedAt or turnDays for SIT release
        toUpdateInventory.status = InventoryStatus.RELEASED;
      } else if (
        inventory.status === InventoryStatus.REVIEW &&
        inventory.verificationRequired === "true"
      ) {
        toUpdateInventory.status = InventoryStatus.STORED;
        toUpdateInventory.verificationRequired = "resolved";
      } else if (
        inventory.status === InventoryStatus.UNLOADED &&
        inventory.verificationRequired === "true"
      ) {
        toUpdateInventory.status = InventoryStatus.STORED;
        toUpdateInventory.verificationRequired = "resolved";
      } else toUpdateInventory.status = inventory.status;

      // toUpdateInventory["status#lotNumber"] =  `${toUpdateInventory.status}#${toUpdateInventory.lotNumber}`;
      // toUpdateInventory["status#accountName#productName#lotNumber"] = `${toUpdateInventory.status}#${toUpdateInventory.accountName}#${toUpdateInventory.productName}#${toUpdateInventory.lotNumber}`;
      // toUpdateInventory["status#accountId#productName#lotNumber"] = `${toUpdateInventory.status}#${toUpdateInventory.accountId}#${toUpdateInventory.productName}#${toUpdateInventory.lotNumber}`;

      delete toUpdateInventory.account;
      delete toUpdateInventory.inboundShipment;
      delete toUpdateInventory.inventoryItems;
      delete toUpdateInventory.location;
      delete toUpdateInventory.product;
      delete toUpdateInventory.reloadYardCommodity;
      delete toUpdateInventory.shipmentLineItems;

      inventoryUpdateList.push({ ...toUpdateInventory });

      if (isRollback) {
        if (inventory.status === InventoryStatus.AGGREGATED) {
          // remove quantity from aggregate
          const response = await API.graphql(
            graphqlOperation(getInventorieAggregate, {
              id: inventory.aggregateInventoryId,
            }),
          );
          aggregateInventory = response.data.getInventorie;
          const toUpdateInventory = {
            ...aggregateInventory,
            aggregateInventoryId: "",
            quantityOnHand:
              aggregateInventory.quantityOnHand - inventory.quantityOnHand,
            updatedAt: updateDate,
          };
          delete toUpdateInventory.account;
          delete toUpdateInventory.inboundShipment;
          delete toUpdateInventory.inventoryItems;
          delete toUpdateInventory.product;
          delete toUpdateInventory.reloadYardCommodity;
          delete toUpdateInventory.shipmentLineItems;

          inventoryUpdateList.push({ ...toUpdateInventory });
        }
      }

      debugger;
      for (const inventoryItem of inventory.inventoryItems.items) {
        // roll back & reset all locations
        if (isRollback) {
          const toDeleteInventoryItem = {
            id: inventoryItem.id,
            inventoryId: inventoryItem.inventoryId,
          };
          inventoryItemDeleteList.push(toDeleteInventoryItem);

          if (inventoryItem.status === InventoryStatus.AGGREGATED) {
            let aggregateItem =
              toUpdateAggregateItemMap[inventoryItem.locationId];
            if (!aggregateItem) {
              aggregateItem = aggregateInventory.inventoryItems.items.find(
                (itm) =>
                  itm.locationId === inventoryItem.locationId &&
                  itm.status === InventoryStatus.STORED,
              );
            }

            // remove quantity from aggregate
            const toUpdateAggregateItem = {
              ...aggregateItem,
              updatedAt: updateDate,
              quantityOnHand:
                aggregateItem.quantityOnHand - inventoryItem.quantityOnHand,
            };
            delete toUpdateAggregateItem.aisle;
            delete toUpdateAggregateItem.area;
            delete toUpdateAggregateItem.inventory;
            delete toUpdateAggregateItem.location;
            delete toUpdateAggregateItem.zone;

            toUpdateAggregateItemMap[inventoryItem.locationId] =
              toUpdateAggregateItem;
          }

          const isOtherLoadType =
            inventoryItem.location.locationLoad?.other.selected;
          // update location capacifyUsed if STRUCTURED and not OTHER, or BULK
          if (
            (inventoryItem.location.type === LocationType.STRUCTURED &&
              !isOtherLoadType) ||
            isBulkLocation(inventoryItem.location)
          ) {
            const updatedLocation =
              toUpdateLocationMap[inventoryItem.locationId];

            if (updatedLocation) {
              // follow-on location encounters
              inventoryItem.location = updatedLocation;
              toUpdateLocationMap[inventoryItem.locationId] = resetCapacityUsed(
                inventory,
                inventoryItem,
              );
            } else {
              // first location encounter
              toUpdateLocationMap[inventoryItem.locationId] = resetCapacityUsed(
                inventory,
                inventoryItem,
              );
            }
          }
        } else {
          // ONLY set inventoryItems to STORED on inbound RELEASED to prevent outbound creation before the whole shipment is completed.
          // This is done to avoid inbound rollback if outbound inventories are committed.
          if (inbound.status === InboundStatus.RELEASED) {
            const toUpdateInventoryItem = {
              ...inventoryItem,
              updatedAt: updateDate,
            };

            if (toUpdateInventory.status === InventoryStatus.SHIPPED) {
              // remove zeroed out inventory on railcar release - for bulk commodities in railcar
              // toUpdateInventoryItem.quantityOnHand = 0;  preserve discrepency count
              toUpdateInventoryItem.status = InventoryStatus.SHIPPED;
              toUpdateInventoryItem.depletedAt = updateDate;
            } else if (inventoryItem.status !== InventoryStatus.AGGREGATED) {
              // bulk is AGGREGATED, all other inventory items are STORED
              toUpdateInventoryItem.status = InventoryStatus.STORED;
            }
            delete toUpdateInventoryItem.aisle;
            delete toUpdateInventoryItem.area;
            delete toUpdateInventoryItem.inventory;
            delete toUpdateInventoryItem.location;
            delete toUpdateInventoryItem.zone;

            inventoryItemUpdateList.push(toUpdateInventoryItem);
          }
        }
      }
    }
  }

  const locationUpdateList = Object.values(toUpdateLocationMap);
  const aggregateItemUpdateList = Object.values(toUpdateAggregateItemMap);
  if (aggregateItemUpdateList?.length > 0)
    inventoryItemUpdateList.push(...aggregateItemUpdateList);

  return {
    inventoryUpdateList,
    inventoryItemUpdateList,
    inventoryItemDeleteList,
    locationUpdateList,
  };
};

export const resetShipmentExceptions = (data) => {
  data.verificationRequired = null;
  data.hasException = false;
  data.unsafeShipment = false;
  data.unsafeShipmentNote = null;
  data.brokenSeal = false;
  data.brokenSealNote = null;
  data.otherException = false;
  data.otherExceptionNote = null;
};

export const handleSendPOD = async (
  data,
  toEmail,
  ccEmail,
  enclosePhotos,
  encloseAttachments,
  notify,
  refresh,
  operatingAccount,
  subject,
) => {
  try {
    // Do not wait emailPOD.
    emailPOD(
      data,
      toEmail,
      ccEmail,
      enclosePhotos,
      encloseAttachments,
      notify,
      subject,
    );

    const historyNote = {
      text: `POD sent to : ${toEmail}`,
      by: window.appUser?.fullName,
      date: new Date(),
      type: NoteType.GENERIC,
    };

    const newHistory = { ...data, history: [...data.history, historyNote] };
    await performUpdateInboundMini(newHistory);
    refresh && refresh();
  } catch (err) {
    logError("InboundShipments:InboundUtils:handleSend", err, notify, null);
  }
};

export const handleLotTypeChange = (value, form, account) => {
  const formData = form.getState().values;
  if (formData.lotNumberOverride !== true && account) {
    form.change("lotNumber", autogenLotNumber(value, formData, account));
  }
};

export const isContainerDropEnabled = (formData, area) =>
  isBulkShipment(formData?.reloadYardCommodity?.commodity, area);

/**
 * SIT railcars are listed on the SIT Cars page, not the regular inbound list,
 * so create/edit saves send the user back here instead of /inboundShipments.
 */
export const SIT_LIST_PATH = "/sitList";

/**
 * Breadcrumb for the inbound edit page. Mirrors getBackPath, except the
 * fallback for a pending or placed SIT railcar is the SIT Cars list: /sitList
 * navigates with react-admin's default rowClick/linkType, neither of which can
 * carry backPath state, and a bookmarked shipment URL has none either. A
 * released SIT car is no longer on the SIT list, so it falls back to the
 * inbound list like any other shipment. An explicit backPath (arriving from
 * Track Management, a deep link, etc.) always wins.
 */
export const getInboundBackPath = (props, record) => {
  const backPath = props?.location?.state?.backPath;
  if (backPath) return backPath;
  const onSitList =
    record?.sit &&
    (record.status === InboundStatus.SIT_PENDING ||
      record.status === InboundStatus.SIT_PLACED);
  return [onSitList ? SIT_LIST_PATH : props.basePath];
};

export const isSITDisabled = (formData) => {
  const trackArea = getTrackArea();
  return (
    !trackArea ||
    formData.containerDrop ||
    formData.stockImport ||
    formData.returnShipment ||
    (formData.id &&
      formData.status !== InboundStatus.ARRIVED &&
      formData.status !== InboundStatus.IN_TRANSIT &&
      formData.status !== InboundStatus.SIT_PENDING)
  );
};

export const handleAreaChange = (areaId, areas, setArea, form, notify) => {
  debugger;
  const formData = form.getState().values;
  const accountId = formData.accountId;
  const reloadYardCommodityId = formData.reloadYardCommodityId;
  const isSIT = formData.sit;
  const newArea = { ...areas.find((itm) => itm.id === areaId) };

  newArea.zones = { ...newArea.zones };
  newArea.zones.items = filterZones(
    newArea.zones.items,
    reloadYardCommodityId,
    accountId,
  );
  setArea(newArea);
  if (newArea.zones.items.length === 1)
    form.change("zoneId", newArea.zones.items[0].id);
  else form.change("zoneId", "");

  if (!isTrack(newArea) && isSIT) {
    form.change("sit", false);
    notify("SIT has been disabled", "warning");
  }
};

export const handleSITChange = (value, form, account, setArea) => {
  if (value) {
    form.change("stockImport", false);
    form.change("secondaryReview", false);
    form.change("containerDrop", false);
    form.change("carrierType", CarrierType.RAIL);
    form.change("lotType", LotType.CARLOT);
    handleLotTypeChange(LotType.CARLOT, form, account);

    const trackArea = getTrackArea();
    if (trackArea) {
      const formData = form.getState().values;
      const reloadYardCommodityId = formData.reloadYardCommodityId;
      trackArea.zones = { ...trackArea.zones };
      trackArea.zones.items = filterZones(
        trackArea.zones.items,
        reloadYardCommodityId,
        account?.id,
      );
      form.change("areaId", trackArea.id);
      setArea(trackArea);

      const zones = trackArea.zones.items;
      if (zones.length === 1) form.change("zoneId", zones[0].id);
      else form.change("zoneId", "");
    } else {
      form.change("areaId", null);
      form.change("zoneId", null);
    }
  }
};

export const getManualChargeAmount = (rule, scopedFormData) => {
  const billingUOM = rule?.billingUOM || "";
  const quantity = Number(scopedFormData?.quantity ?? 0);

  if (!rule || !billingUOM) return false;
  const rate = getUnitPrice(rule.service, billingUOM, null);
  if (rate == null || !quantity || quantity <= 0) return false;
  const charge = formatCurrency(quantity * rate, 2);

  return charge != null ? `Est. Charge: ${charge}` : false;
};

export const applyManualRuleDefaultQuantity = ({
  value,
  applicableRules,
  formData,
  scopedFormData,
  form,
  getSource,
}) => {
  if (!value) return;
  const ruleId = value?.target?.value || value;
  const rule = applicableRules.find((r) => r.id === ruleId);
  if (!rule) return;

  const currentQty = scopedFormData?.quantity;
  const totalQuantity = calcTotalQuantity(formData);
  let nextQty = currentQty;

  if (!currentQty || currentQty <= 0) {
    if (
      (rule.event === ChargeEvent.STORAGE &&
        (rule.frequency === StorageFrequency.ON_RELEASE ||
          rule.frequency === StorageFrequency.ON_PULL)) ||
      rule.billingUOM === Unit.CAR ||
      rule.billingUOM === Unit.TL
    ) {
      nextQty = 1;
    } else if (
      Number(totalQuantity) > 0 &&
      rule.billingUOM === formData?.totalUOM
    ) {
      nextQty = totalQuantity;
    }
  }

  if (nextQty != null && nextQty !== currentQty) {
    form.change(getSource("quantity"), nextQty);
  }
};

export const buildManualChargeOps = (
  shipment,
  recordCharges = [],
  formCharges = null,
) => {
  const now = new Date().toISOString();
  const incoming = formCharges || [];

  const createList = [];
  const updateList = [];
  const deleteList = [];
  const seen = new Set();

  incoming.forEach((mc, idx) => {
    const chargeRuleId = mc?.chargeRuleId || mc?.chargeRule?.id;
    const quantity = Number(mc?.quantity ?? 0);
    if (!chargeRuleId || !quantity || quantity <= 0) return;

    // Only MANUAL execution rules should be persisted here
    if (mc?.execution && mc.execution !== BillingAutomation.MANUAL) return;

    const base = {
      id: mc.id || uuidv4(),
      owner: shipment.owner,
      groups: shipment.groups,
      reloadYardId: shipment.reloadYardId,
      parentId: shipment.id,
      parentType: ResourceType.INBOUND,
      chargeRuleId,
      quantity,
      sortKey: mc.sortKey ?? idx + 1,
      invoiceServiceId: mc.invoiceServiceId || null,
      updatedAt: now,
    };

    if (mc.id) {
      updateList.push(base);
      seen.add(mc.id);
    } else {
      createList.push({ ...base, createdAt: now });
    }
  });

  (recordCharges || []).forEach((mc) => {
    if (!mc?.id) return;
    if (!seen.has(mc.id)) deleteList.push({ id: mc.id });
  });

  return { createList, updateList, deleteList };
};

// Single-call inbound create: parent + inventories + inventory items +
// photos + track car + manual charges in one lambda transaction set.
// The lambda is the sole writer of the parent InboundShipment (shipped as
// inboundCreateInput); AppSync no longer creates the record, so a lambda
// failure leaves no orphan parent and a retry mints a fresh id. `data` must be
// the UNSANITIZED record — sanitizeInbound/sanitizeInboundLambda strip the
// inventories/manualCharges @connection objects this assembly reads.
export const createLambdaInbound = async (
  data,
  { photos = [], account = null, selectedTimeslot = null } = {},
) => {
  const effectiveAccount = account;
  const inventories = data.inventories?.items || [];
  const manualCharges = data.manualCharges?.items || [];

  const trackCarCreateInput =
    data.carrierType === CarrierType.RAIL
      ? buildTrackCarCreateInput(data)
      : null;
  const inventoryCreateList = [];
  const inventoryItemCreateList = [];
  const photoCreateList = photos.map((p) => ({
    ...p,
    id: uuidv4(),
    __typename: "Photo",
    owner: data.owner,
    parentId: data.id,
    createdAt: data.createdAt,
    updatedAt: data.createdAt,
  }));

  const history = [
    {
      text: `Created Inventory.`,
      type: NoteType.GENERIC,
      date: data.createdAt,
      by: window.appUser?.fullName,
    },
  ];

  inventories.forEach((item, index) => {
    const input = {
      id: uuidv4(),
      __typename: "Inventory",
      owner: data.owner,
      createdAt: data.createdAt,
      updatedAt: data.createdAt,
      active: data.active,
      groups: data.groups,
      status: InventoryStatus.ARRIVED,
      sortKey: index + 10001,
      accountName: data.accountName,
      poNumber: item.poNumber || data.poNumber,
      areaId: data.areaId,
      zoneId: data.zoneId,
      aisleId: null,
      locationId: null,
      locationFullName: null,
      productName: item.productName,
      receivedAt: null,
      inventoryUOM: item.inventoryUOM || data.totalUOM,
      quantityAdvised: item.quantityAdvised,
      quantityCommitted: 0,
      quantityOnHand: 0,
      quantityPhysical: 0,
      pcs: item.pcs,
      pcsHeight: item.pcsHeight,
      pcsWidth: item.pcsWidth,
      pcsLength: item.pcsLength,
      accountId: data.accountId,
      reloadYardId: data.reloadYardId,
      inboundShipmentId: data.id,
      history: history,
    };
    input.productCode = item.productCode ? item.productCode : "";
    input.productId = item.productId ? item.productId : "";
    input.reloadYardCommodityId =
      item.productId && item.reloadYardCommodityId
        ? item.reloadYardCommodityId
        : data.reloadYardCommodityId;

    if (item.weight) {
      input.weight = item.weight;
      input.weightUnits = getWeightUOM(effectiveAccount, item);
      input.weightUOM = getWeightUOM(effectiveAccount, item);
    }
    if (data?.containerNumber) {
      input.containerNumber = data.containerNumber;
    }

    input.lotType = data.lotType ? data.lotType : "";
    input.lotNumber = item.lotNumber || (data.lotType ? data.lotNumber : "");
    input.railCarNumber = item.railCarNumber || data.railCarNumber;
    input.normalizedPoNumber = normalizeValue(input.poNumber);
    input.normalizedRailCarNumber = normalizeValue(input.railCarNumber);

    input.customFields = getCustomFieldsToSave(
      item.customFields,
      effectiveAccount.inventoryCustomFields,
    );

    if (item.locationFullName && data.stockImport) {
      input.status = InventoryStatus.STORED;
      input.aisleList = [item.aisleId];
      input.quantityReceived = item.quantityAdvised;
      input.quantityOnHand = item.quantityAdvised;
      input.receivedAt = data.createdAt;
      input.turnDays = 0;

      // The row carries its own location from the import file, so the stock
      // never sat in the shipment's area: stamp the header with the row's
      // placement instead of the inbound's. locationId is what makes it stick
      // -- transformLamdaInventories resets an inventory with no location of
      // its own back to inbound.areaId on every later save of this inbound.
      input.areaId = item.areaId ?? data.areaId;
      input.zoneId = item.zoneId ?? data.zoneId;
      input.aisleId = item.aisleId ?? null;
      input.locationId = item.locationId ?? null;

      const inventoryIteminput = {
        id: uuidv4(),
        __typename: "InventoryItem",
        owner: data.owner,
        // active: data.active, // not yet in schema
        groups: data.groups,
        createdAt: data.createdAt,
        updatedAt: data.createdAt,
        inventoryId: input.id,
        reloadYardId: data.reloadYardId,
        inboundShipmentId: data.id,
        accountId: data.accountId,
        status: InventoryStatus.STORED,
        quantityOnHand: item.quantityAdvised,
        quantityCommitted: 0,
        unloadedBy: window.appUser?.fullName,
        unloadedAt: data.createdAt,
        locationFullName: item.location
          ? getLocationFullName(item.location)
          : item.locationFullName || "",
        locationId: item.locationId,
        aisleId: item.aisleId,
        zoneId: item.zoneId,
        areaId: item.areaId,
      };

      input.locationFullName = inventoryIteminput.locationFullName || null;

      inventoryItemCreateList.push(inventoryIteminput);
    }

    inventoryCreateList.push(input);
  });

  const { createList: manualChargeCreateList } = buildManualChargeOps(
    data,
    [],
    manualCharges,
  );

  let timeslotCreateInput = null;
  if (selectedTimeslot) {
    timeslotCreateInput = createLambdaAppointment(
      selectedTimeslot,
      data.id,
      null,
      data.accountId,
      data.accountName,
    );
  }

  const response = await API.post("transREST", "/inboundCreate", {
    body: {
      // Full-item parent write — the lambda is the sole writer.
      // sanitizeInboundLambda strips @connection objects but KEEPS the
      // client-stamped id/owner/createdAt/updatedAt so the direct DynamoDB
      // Put persists them (AppSync no longer stamps them).
      inboundCreateInput: {
        ...sanitizeInboundLambda({ ...data }),
        // Same-transaction schedule link (mirrors outboundCreateInput).
        ...(timeslotCreateInput ? { timeslotId: timeslotCreateInput.id } : {}),
      },
      timeslotCreateInput: timeslotCreateInput || null,
      trackCarCreateInput: trackCarCreateInput,
      inventoryCreateList: inventoryCreateList,
      inventoryItemCreateList: inventoryItemCreateList,
      photoCreateList: photoCreateList,
      manualChargeCreateList,
      user: window.appUser.email,
      reloadYardId: window.appUser.reloadYardId,
    },
  });
  if (response.error) throw response.error;

  if (timeslotCreateInput) {
    // Mirror OutboundUtils: the caller's record feeds the RA cache; without
    // the schedule link a pre-refetch re-save takes the wrong create path.
    data.timeslotId = timeslotCreateInput.id;
  }

  return response;
};
